import { type App, type InjectionKey, inject } from 'vue';
import type { Locale, CurrencyCode } from '../../types';
import { defineCurrencySymbol, getTypedKeysFromObject } from '../../helpers';

import type {
    ResourcesSource,
    TranslationKey, 
    InterpolationParams, 
    Translations,
    Options,
    RegExpCacheMap,
    CorePostProcessorKey,
    PluralizeFn,
    PluralizeTimeUnitFn,
    PluralizeDurationByMaxUnit,
    ScopedUtils,
} from './shared/types';
import {
    NO_TRANSLATE,
    TIMEZONE_UTC,
    MS_IN_MINUTE,
    MS_IN_HOUR,
    MS_IN_DAY,
    MS_IN_MONTH,
} from './shared/constants';
import {
    DEFAULT_DATETIME_SETTINGS,
    DEFAULT_NUMBER_SETTINGS,
    DEFAULT_OPTIONS,
    DEFAULT_CULTURES
} from './shared/defaults';
import {
    validateLocale,
    validateResourcesSource,
    validateTranslationKey
} from './shared/utils/validation';
import {
    parseResourcesSourceToTranslations,
    interpolateParameters,
} from './shared/utils/translationHelpers';
import { createTimeUnitPluralizer } from './shared/utils/createTimeUnitPluralizer';
import { createPostProcessor } from './shared/post-processors';
import { buildNumber } from './shared/utils/buildNumber';

import {
    type DateTimeFormat,
    type DateTimeFormatFunction,
    type DateTimeSettings,
    type DateTimeFormaterOptions,
    createDateTimeFormatter,
} from './formatters/date-time';

import {
    type NumberFormat,
    type NumberFormatFunction,
    type NumberSettings,
    type NumberFormaterOptions,
    createNumberFormatter,
} from './formatters/number';

import { corePostProcessors } from './corePostProcessors';


export class I18nService { 
    private readonly locale: Locale;
    private readonly translations: Translations;
    private readonly options: Options;

    private readonly dateTimeSettings: DateTimeSettings;
    private readonly dateTimeFormatter: DateTimeFormatFunction;

    private readonly numberSettings: NumberSettings;
    private readonly numberFormatter: NumberFormatFunction;

    private readonly placeholderRegExpCache: RegExpCacheMap;
    private readonly missingKeysRegistry: Map<string, string>;

    private readonly timeUnitPluralizer: PluralizeTimeUnitFn;
    private readonly pluralRules: Intl.PluralRules;
    private readonly postProcessorsPipe: ReturnType<typeof createPostProcessor<CorePostProcessorKey, null>>;

    // Important to keep Symbol.for() to work in multiple webpack chunks
    // simple Sybmol() creates new instance in every chunk
    public static readonly injectionKey = Symbol.for('__I18N_SERVICE_KEY__') as InjectionKey<I18nService>;
    
    constructor(
        locale: Locale,
        resourcesSource: ResourcesSource,

        dateTimeSettings: Partial<DateTimeSettings>,
        numberSettings: Partial<NumberSettings>,

        options: Partial<Options> = {}
    ) {
        // Validate all input parameters early
        validateLocale(locale);
        validateResourcesSource(resourcesSource);

        // Initialize core properties
        this.locale = DEFAULT_CULTURES[locale] || locale;

        this.translations = {
            [this.locale]: parseResourcesSourceToTranslations(resourcesSource)
        };

        this.options = {
            ...DEFAULT_OPTIONS,
            ...options,
        };

        // Initialize cache and registry
        this.placeholderRegExpCache = new Map();
        this.missingKeysRegistry = new Map();

        // Configure date/time settings
        this.dateTimeSettings = {
            ...DEFAULT_DATETIME_SETTINGS,
            ...dateTimeSettings,
        };
        
        if (this.dateTimeSettings.useTimeZoneOffset) {
            this.dateTimeSettings.timeZone = TIMEZONE_UTC;
        }

        // Configure number settings
        this.numberSettings = {
            ...DEFAULT_NUMBER_SETTINGS,
            ...numberSettings,
        };

        // Create Intl instances
        /*
            Локаль — нормализованная (`this.locale`: en → en-IE), как у дат и чисел.
            Для длинных подписей разницы нет, для коротких есть: en «2 hr», en-IE «2 hrs».
        */
        this.timeUnitPluralizer = createTimeUnitPluralizer(this.locale);

        this.pluralRules = new Intl.PluralRules(locale);

        // create post-processor for class-own methods
        this.postProcessorsPipe = createPostProcessor(corePostProcessors, locale);


        //
        // Create formatters
        //

        const scopedUtils: ScopedUtils = {
            pluralizeFn: this.pluralize,
            pluralizeTimeUnit: this.pluralizeTimeUnit,
            pluralizeDurationByMaxUnit: this.pluralizeDurationByMaxUnit,
        }

        this.dateTimeFormatter = createDateTimeFormatter(locale, this.dateTimeSettings, scopedUtils);
        this.numberFormatter = createNumberFormatter(locale, this.numberSettings, scopedUtils);
    };


    //
    // locales management
    //
/*
    // TODO:
    // пока не понятна логика, т.к. есть вероятность что локаль не будет меняться одна
    // или при смене локали нужно будет грузить ресурсы
    // нужно будет делать сеттеры под валюту и таймзону

    public setLocale = (locale: Locale): void => {
        if (!this.translations[locale]) {
            console.warn(`Translations for locale '${locale}' not found. Falling back to current locale.`);
            return;
        }

        this.locale = locale;
        this.dateTimeFormatter = createDateTimeFormatter(locale);
        this.numberFormatter = createNumberFormatter(locale);
    };
*/
    public getCurrentLocale = (): Locale => {
        return this.locale;
    };

    public getAvailableLocales = (): Locale[] => {
        return getTypedKeysFromObject(this.translations);
    };

    public getCurrencyCode = (): CurrencyCode => {
        return this.numberSettings.currency;
    };

    public getCurrencySymbol = (): string => {
        return defineCurrencySymbol(this.numberSettings.currency);
    };


    //
    // formatting
    //

    public formatDate = (
        value: number | string | Date,
        format?: DateTimeFormat,
        options?: DateTimeFormaterOptions
    ): string => {
        try {
            return this.dateTimeFormatter(
                value,
                format ?? this.dateTimeSettings.defaultFormat,
                options
            );
        }
        catch (error) {
            console.error('Error formatting date:', error);
            return String(value);
        }
    };

    public formatNumber = (
        value: number | string,
        format?: NumberFormat,
        options?: NumberFormaterOptions
    ): string => {
        try {
            return this.numberFormatter(
                value,
                format ?? this.numberSettings.defaultFormat,
                options
            );
        }
        catch (error) {
            console.error('Error formatting number:', error);
            return String(value);
        }
    };


    //
    // translation
    //

    private logFallbackError = (key: string, fallbackMEssage: string = ''): void => {
        this.missingKeysRegistry.set(key, fallbackMEssage);
    };

    public translate = (
        key: TranslationKey, 
        fallbackValue?: string,
        params?: InterpolationParams
    ): string => {
        // Early validation
        validateTranslationKey(key);

        const localeTranslations = this.translations[this.locale];

        if (!localeTranslations) {
            throw new Error('Wrong state of the I18n service. There are no loaded Resources for the given Locale.');
        }

        let translation = localeTranslations.get(key);

        // additional type validation of returned value
        if (!translation || typeof translation !== 'string') {
            this.logFallbackError(key, fallbackValue);

            if (this.options.disableFallbacks) {
                console.error(`Missing translation: "${key}". Fallbacks disabled.`);
                return NO_TRANSLATE;
            }

            if (!fallbackValue) {
                console.error(`Missing translation: "${key}". Default value was not provided.`);
                return NO_TRANSLATE;
            }

            translation = fallbackValue;
        }

        if (!params) {
            return translation;
        }

        return interpolateParameters(translation, params, this.placeholderRegExpCache);
    };


    //
    // helpers
    //

    public pluralize: PluralizeFn = (n, forms) => {
        let _n: number = 0;

        try {
            _n = buildNumber(n);
        }
        catch (error) {
            console.error('I18nService.pluralize() invalid input:', error);
            return '';
        }

        const rule = this.pluralRules.select(_n);
    
        let word = forms[2];
    
        if (rule === 'one') {
            word = forms[0];
        }
    
        if (rule === 'few') {
            word = forms[1];
        }
    
        return word;
    }

    /**
     * Returns a locale-aware pluralized string for a given count and unit.
     * Uses Intl.NumberFormat({ style: 'unit' }) to produce correct grammatical forms.
     * @param count - The numeric value.
     * @param unit - The time unit (e.g. 'day', 'hour', 'minute', 'second').
     * @param options - `unitDisplay`: 'long' (default) | 'short' | 'narrow'.
     * @returns Pluralized string, e.g. "5 дней", "1 day", "2 дня"; short — "5 дн.", "3 ч", "2 hrs".
     */
    public pluralizeTimeUnit: PluralizeTimeUnitFn = (count, unit, options) => {
        let _count: number = 0;

        try {
            _count = buildNumber(count);
        }
        catch (error) {
            console.error('I18nService.pluralizeTimeUnit() invalid input:', error);
            return '';
        }

        return this.postProcessorsPipe(
            this.timeUnitPluralizer(_count, unit, options),
            'pluralizeTimeUnit',
            null
        );
    };

    public pluralizeDurationByMaxUnit: PluralizeDurationByMaxUnit = (durationMs) => {
        let _durationMs: number = 0;

        try {
            _durationMs = buildNumber(durationMs);
        }
        catch (error) {
            console.error('I18nService.pluralizeDurationByMaxUnit() invalid input:', error);
            return '';
        }

        if (_durationMs < 0) return '';

        const diffMonths = Math.floor(_durationMs / MS_IN_MONTH);
        if (diffMonths >= 1) {
            return this.pluralizeTimeUnit(diffMonths, 'month');
        }

        const diffDays = Math.floor(_durationMs / MS_IN_DAY);
        if (diffDays >= 1) {
            return this.pluralizeTimeUnit(diffDays, 'day');
        }

        const diffHours = Math.floor(_durationMs / MS_IN_HOUR);
        if (diffHours >= 1) {
            return this.pluralizeTimeUnit(diffHours, 'hour');
        }

        const diffMinutes = Math.floor(_durationMs / MS_IN_MINUTE);
        if (diffMinutes >= 1) {
            return this.pluralizeTimeUnit(diffMinutes, 'minute');
        }

        return this.pluralizeTimeUnit(1, 'minute');
    };


    //
    // short aliases
    //
    
    public readonly $d = this.formatDate;
    public readonly $n = this.formatNumber;
    public readonly $t = this.translate;


    //
    // Vue plugin installer
    //

    public install = (app: App): void => {
        app.provide(I18nService.injectionKey, this);
    };


    //
    // Vue composition function for getting I18nService instance
    //

    public static use(): I18nService {
        const service = inject(I18nService.injectionKey) as I18nService;

        if (!service) {
            throw new Error('I18nService plugin is not installed. Make sure to call app.use(i18nService) before using this composition function.');
        }

        return service;
    };
}
